import {useState} from 'react'
import PropTypes from 'prop-types'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import {ClockAlert, LogOut, RefreshCw} from 'lucide-react'

import {useAuth} from 'components/SessionAuth'
import {isTrialAccessExpired} from 'utils/trial'

export default function TrialAccessGate({children}) {
  const {userData, refreshUserData, logout} = useAuth()
  const [refreshing, setRefreshing] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')

  if (!isTrialAccessExpired(userData)) return children

  const handleRefresh = async () => {
    if (refreshing) return
    setRefreshing(true)
    setStatusMessage('')

    try {
      const result = await refreshUserData()
      if (!result?.success) {
        setStatusMessage(result?.error || 'Unable to refresh your account. Please try again.')
      } else if (isTrialAccessExpired(result.userData)) {
        setStatusMessage('Your account is still awaiting approval. Please contact support for assistance.')
      }
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f7f9fc',
        px: 3,
        py: 6,
      }}
    >
      <Box sx={{width: '100%', maxWidth: 520, textAlign: 'center'}}>
        <Box
          sx={{
            width: 64,
            height: 64,
            mx: 'auto',
            mb: 3,
            display: 'grid',
            placeItems: 'center',
            color: '#b42318',
            bgcolor: '#fee4e2',
            borderRadius: 2,
          }}
        >
          <ClockAlert size={32} aria-hidden="true" />
        </Box>

        <Typography variant="h3" component="h1" sx={{mb: 1.5, fontSize: {xs: 28, sm: 34}}}>
          Trial access expired
        </Typography>
        <Typography sx={{color: 'text.secondary', fontSize: 17, lineHeight: 1.6}}>
          Your trial access has ended. Contact support to continue using Sparkle Timekeeping.
        </Typography>
        <Typography sx={{mt: 1.5, color: 'text.secondary', lineHeight: 1.6}}>
          If your account has already been approved, click Refresh to check your access.
        </Typography>

        {statusMessage ? (
          <Alert severity="warning" sx={{mt: 3, textAlign: 'left'}}>
            {statusMessage}
          </Alert>
        ) : null}

        <Box
          sx={{
            mt: 4,
            display: 'flex',
            flexDirection: {xs: 'column', sm: 'row'},
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={handleRefresh}
            disabled={refreshing}
            startIcon={
              refreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshCw size={18} />
            }
            sx={{minWidth: 150, borderRadius: 1}}
          >
            {refreshing ? 'Refreshing' : 'Refresh'}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            onClick={logout}
            startIcon={<LogOut size={18} />}
            sx={{minWidth: 150, borderRadius: 1}}
          >
            Logout
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

TrialAccessGate.propTypes = {
  children: PropTypes.node.isRequired,
}
